import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import SeletorFuncionario from "../components/ui/SeletorFuncionario";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../components/ui/ThFiltro";

const TIPO_LABEL = { ferias: "Férias", atestado: "Atestado médico", outro: "Outro" };
const TIPO_TONE = { ferias: "primary", atestado: "warning", outro: "neutral" };

function formatarData(iso) {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

function selectStyle() {
  return { width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "11px 14px", fontSize: 14, background: COLORS.bg, color: COLORS.text };
}
function labelStyle() {
  return { fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 };
}

export default function Afastamentos() {
  const { token } = useAuth();
  const [afastamentos, setAfastamentos] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroInicio, setFiltroInicio] = useState("");
  const [filtroFim, setFiltroFim] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroObservacao, setFiltroObservacao] = useState("");

  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState({ funcionario_id: "", tipo: "ferias", data_inicio: "", data_fim: "", observacao: "" });
  const [arquivo, setArquivo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [a, f] = await Promise.all([
        apiFetch("GET", "/api/afastamentos", undefined, token),
        apiFetch("GET", "/api/funcionarios", undefined, token),
      ]);
      setAfastamentos(a);
      setFuncionarios(f.filter((x) => x.ativo));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNovo = () => {
    setForm({ funcionario_id: "", tipo: "ferias", data_inicio: "", data_fim: "", observacao: "" });
    setArquivo(null);
    setErro("");
    setModalAberto(true);
  };

  const salvar = async () => {
    setErro("");
    if (!form.funcionario_id) { setErro("Selecione o funcionário"); return; }
    if (!form.data_inicio || !form.data_fim) { setErro("Informe as datas de início e fim"); return; }
    setSalvando(true);
    try {
      // Ordem importa: funcionario_id precisa vir ANTES do arquivo no
      // multipart pro backend já saber em qual pasta salvar o anexo.
      const fd = new FormData();
      fd.append("funcionario_id", form.funcionario_id);
      fd.append("data_inicio", form.data_inicio);
      fd.append("data_fim", form.data_fim);
      fd.append("tipo", form.tipo);
      fd.append("observacao", form.observacao);
      if (arquivo) fd.append("anexo", arquivo);
      await apiFetch("POST", "/api/afastamentos", fd, token);
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (a) => {
    if (!window.confirm(`Remover "${TIPO_LABEL[a.tipo]}" de ${a.funcionario_nome} (${formatarData(a.data_inicio)} a ${formatarData(a.data_fim)})?`)) return;
    try {
      await apiFetch("DELETE", `/api/afastamentos/${a.id}`, undefined, token);
      await carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const listaFiltrada = afastamentos.filter((a) => {
    if (filtroTipo && a.tipo !== filtroTipo) return false;
    if (filtroNome && !a.funcionario_nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    if (filtroInicio && a.data_inicio !== filtroInicio) return false;
    if (filtroFim && a.data_fim !== filtroFim) return false;
    if (filtroStatus && (a.status || "pendente") !== filtroStatus) return false;
    if (filtroObservacao && !(a.observacao || "").toLowerCase().includes(filtroObservacao.toLowerCase())) return false;
    return true;
  });
  const filtrosAtivos = Boolean(filtroTipo || filtroNome || filtroInicio || filtroFim || filtroStatus || filtroObservacao);
  const limparFiltros = () => {
    setFiltroTipo(""); setFiltroNome(""); setFiltroInicio(""); setFiltroFim(""); setFiltroStatus(""); setFiltroObservacao("");
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Afastamentos/Férias
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Lance férias e afastamentos de qualquer funcionário, passados ou futuros. Diferente de feriado, esses dias
            contam como folga mesmo para quem tem escala 12x36.
          </p>
        </div>
        <Btn onClick={abrirNovo}>+ Novo afastamento</Btn>
      </div>

      <BarraResumoFiltro
        total={listaFiltrada.length}
        totalGeral={afastamentos.length}
        unidade="registro(s)"
        filtrosAtivos={filtrosAtivos}
        onLimpar={limparFiltros}
      />

      <Card style={{ padding: 0, marginBottom: 20 }}>
        {loading ? (
          <EstadoCarregando padding={24} />
        ) : listaFiltrada.length === 0 ? (
          <EstadoVazio icon="heart" texto={filtrosAtivos ? "Nenhum afastamento encontrado com esse filtro." : "Nenhum afastamento ou férias cadastrado ainda."} />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1.5px solid ${COLORS.border}` }}>
                  <ThFiltroTexto label="Funcionário" value={filtroNome} onChange={setFiltroNome} placeholder="Buscar nome..." minWidth={170} />
                  <ThFiltroSelect
                    label="Tipo" value={filtroTipo} onChange={setFiltroTipo} minWidth={140}
                    opcoes={[{ value: "", label: "Todos" }, { value: "ferias", label: "Férias" }, { value: "atestado", label: "Atestado médico" }, { value: "outro", label: "Outro" }]}
                  />
                  <ThFiltroTexto label="Início" type="date" value={filtroInicio} onChange={setFiltroInicio} minWidth={140} />
                  <ThFiltroTexto label="Fim" type="date" value={filtroFim} onChange={setFiltroFim} minWidth={140} />
                  <ThFiltroSelect
                    label="Status" value={filtroStatus} onChange={setFiltroStatus} minWidth={130}
                    opcoes={[{ value: "", label: "Todos" }, { value: "pendente", label: "Pendente" }, { value: "aprovado", label: "Aprovado" }, { value: "rejeitado", label: "Rejeitado" }]}
                  />
                  <ThFiltroTexto label="Observação" value={filtroObservacao} onChange={setFiltroObservacao} placeholder="Buscar..." minWidth={160} />
                  <ThSimples label="Ação" alinhamento="right" />
                </tr>
              </thead>
              <tbody>
                {listaFiltrada.map((a) => (
                  <tr key={a.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 14px", fontSize: 13.5, fontWeight: 700, color: COLORS.text, whiteSpace: "nowrap" }}>{a.funcionario_nome}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <Badge tone={TIPO_TONE[a.tipo] || "neutral"}>{TIPO_LABEL[a.tipo] || a.tipo}</Badge>
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>{formatarData(a.data_inicio)}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>{formatarData(a.data_fim)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <Badge tone={a.status === "aprovado" ? "success" : a.status === "rejeitado" ? "danger" : "warning"}>
                        {a.status === "aprovado" ? "Aprovado" : a.status === "rejeitado" ? "Rejeitado" : "Pendente"}
                      </Badge>
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, maxWidth: 260 }}>{a.observacao || "—"}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>
                      <button
                        onClick={() => excluir(a)}
                        style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.danger, cursor: "pointer" }}
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={modalAberto} onClose={() => setModalAberto(false)} title="Novo afastamento" width={440}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle()}>Funcionário</label>
            <SeletorFuncionario funcionarios={funcionarios} value={form.funcionario_id} onChange={(id) => setForm({ ...form, funcionario_id: id })} />
          </div>

          <div>
            <label style={labelStyle()}>Tipo</label>
            <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} style={selectStyle()}>
              <option value="ferias">Férias</option>
              <option value="atestado">Atestado médico</option>
              <option value="outro">Outro</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <Input label="Início" type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <Input label="Fim" type="date" value={form.data_fim} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} />
            </div>
          </div>
          <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: 0 }}>
            Pode ser um período passado (pra regularizar) ou futuro (pra já deixar programado, ex: férias do mês que vem).
          </p>

          <div>
            <label style={labelStyle()}>Observação (opcional)</label>
            <textarea
              value={form.observacao}
              onChange={(e) => setForm({ ...form, observacao: e.target.value })}
              placeholder="Detalhes, se precisar..."
              rows={3}
              style={{ ...selectStyle(), resize: "vertical", fontFamily: "inherit" }}
            />
          </div>

          <div>
            <label style={labelStyle()}>Anexo (PDF ou foto, opcional)</label>
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) => setArquivo(e.target.files[0] || null)}
              style={{ fontSize: 13 }}
            />
          </div>

          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
            </div>
          )}

          <Btn onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Cadastrar"}</Btn>
        </div>
      </Modal>
    </div>
  );
}
